import ButtonT from '@components/tailwind-components/Button/ButtonT';
import InputT from '@components/tailwind-components/Input/InputT';
import RadioInputT from '@components/tailwind-components/Input/RadioInputT';
import SelectInputT from '@components/tailwind-components/Input/SelectInputT';
import LabelT from '@components/tailwind-components/Label/LabelT';
import type { TConversationDetailsSchema } from '@conversed/rest/src/contracts/llm-usages/llm-usages.contract';
import { PERMISSIONS } from '@conversed/shared/src/central/constants/permissions';
import { useModal } from '@hooks/modal/useModal';
import { useConversationKeywords } from '@hooks/settings/conversation-keywords/useConversationKeywords';
import { useConversationKeywordsActions } from '@hooks/settings/conversation-keywords/useConversationKeywordsActions';
import { useConversationKeywordsInit } from '@hooks/settings/conversation-keywords/useConversationKeywordsInit';
import { usePermissions } from '@hooks/usePermissions';
import { mainActions } from '@store/reducers/mainHandler';
import type React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import KeywordBadge from '../KeywordBadge';

type KeywordMode = 'select' | 'new';
const MAX_KEYWORD_LENGTH = 50;

type Props = {
  conversation: TConversationDetailsSchema;
  onSave: (keywords: string[]) => void;
};

const ConversationKeywordsModal: React.FC<Props> = ({
  conversation,
  onSave,
}) => {
  const { t } = useTranslation();
  const { hideModal } = useModal();
  const { hasPermissions } = usePermissions();
  const { updateConversationKeywords } = useConversationKeywordsActions();
  const dispatch = useDispatch();

  // Whether the current user can create new keywords from within this modal
  const canCreateKeyword = hasPermissions([
    PERMISSIONS.CONVERSATION_KEYWORDS.CONVERSATION_KEYWORDS_CREATE,
  ]);

  useConversationKeywordsInit(true);
  const { keywords: storedKeywords } = useConversationKeywords();

  // Resolve stored keyword IDs to their text representations using the pool.
  // The pool is already loaded by the parent component's useConversationKeywordsInit call.
  const [pendingKeywords, setPendingKeywords] = useState<string[]>(() =>
    (conversation.keywords ?? [])
      .map(
        (assignedKeywordId) =>
          storedKeywords.find(
            (poolEntry) => poolEntry._id.toString() === assignedKeywordId,
          )?.keyword,
      )
      .filter((keywordText): keywordText is string => Boolean(keywordText)),
  );
  const [keywordMode, setKeywordMode] = useState<KeywordMode>('select');
  const [selectedKeyword, setSelectedKeyword] = useState('');
  const [newKeyword, setNewKeyword] = useState('');
  const [newKeywordError, setNewKeywordError] = useState('');

  const poolKeywords = useMemo(
    () => storedKeywords.map((keywordDto) => keywordDto.keyword),
    [storedKeywords],
  );

  // Exclude keywords already in the pending list from the select dropdown
  // so the user can't accidentally add duplicates via the select mode
  const availableFromPool = useMemo(
    () => poolKeywords.filter((keyword) => !pendingKeywords.includes(keyword)),
    [poolKeywords, pendingKeywords],
  );

  const selectOptions = useMemo(
    () =>
      availableFromPool.map((keyword) => ({
        value: keyword,
        description: keyword,
      })),
    [availableFromPool],
  );

  const validateNewKeyword = useCallback(
    (value: string): string => {
      const trimmed = value.trim();
      if (value.length > 0 && trimmed.length === 0) {
        return t('train-ai.keywords.error.whitespace-only');
      }
      if (trimmed.length > MAX_KEYWORD_LENGTH) {
        return t('train-ai.keywords.error.max-length', {
          max: MAX_KEYWORD_LENGTH,
        });
      }
      if (pendingKeywords.includes(trimmed)) {
        return t('train-ai.keywords.error.duplicate');
      }
      return '';
    },
    [t, pendingKeywords],
  );

  const handleNewKeywordChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setNewKeyword(value);
      setNewKeywordError(validateNewKeyword(value));
    },
    [validateNewKeyword],
  );

  const handleAdd = useCallback(() => {
    let keyword: string;
    if (keywordMode === 'new') {
      const error = validateNewKeyword(newKeyword);

      if (error) {
        setNewKeywordError(error);
        return;
      }

      keyword = newKeyword.trim();
    } else {
      keyword = selectedKeyword;
    }

    // Guard against empty input and duplicates that may slip through
    // when the user selects and then immediately clicks Add on an empty select
    if (!keyword || pendingKeywords.includes(keyword)) {
      return;
    }

    setPendingKeywords((prev) => [...prev, keyword]);
    setSelectedKeyword('');
    setNewKeyword('');
    setNewKeywordError('');
  }, [
    keywordMode,
    newKeyword,
    selectedKeyword,
    pendingKeywords,
    validateNewKeyword,
  ]);

  const handleRemove = useCallback((keyword: string) => {
    setPendingKeywords((prev) =>
      prev.filter((existingKeyword) => existingKeyword !== keyword),
    );
  }, []);

  const handleSave = useCallback(async () => {
    const result = await updateConversationKeywords(
      conversation._id,
      pendingKeywords,
    );

    // Merge any newly created keywords into the Redux pool without clearing it.
    // The API returns the full upserted keyword objects; we keep only those that
    // are not already present so we don't create duplicates in the store.
    if (result?.upsertedKeywords?.length) {
      const existingIds = new Set(
        storedKeywords.map((poolEntry) => poolEntry._id.toString()),
      );
      // Only merge new keywords that were created in this update, not ones that were already in the pool, to avoid duplicates.
      const newKeywords = result.upsertedKeywords.filter(
        (upsertedKeyword) => !existingIds.has(upsertedKeyword._id.toString()),
      );

      if (newKeywords.length > 0) {
        const mergedPool = [...storedKeywords, ...newKeywords].sort(
          (firstKeyword, secondKeyword) =>
            firstKeyword.keyword.localeCompare(secondKeyword.keyword),
        );
        dispatch(mainActions.setConversationKeywords(mergedPool));
      }
    }

    // Use the IDs returned by the API so local table state stores IDs (not texts)
    onSave(result?.keywords ?? []);
    hideModal();
  }, [
    conversation._id,
    pendingKeywords,
    storedKeywords,
    updateConversationKeywords,
    dispatch,
    onSave,
    hideModal,
  ]);

  return (
    <div className='tailwind-modal-form'>
      <div className='tailwind-modal-body'>
        <div className='col-span-1 flex flex-col gap-4'>
          {/* Assigned keywords */}
          <div>
            <LabelT text={t('train-ai.keywords.assigned')} />
            <div className='flex flex-wrap gap-1.5 max-h-[300px] overflow-y-auto min-h-[22px] mt-1'>
              {pendingKeywords.length === 0 ? (
                <span className='text-xs text-gray-400 self-center'>
                  {t('train-ai.keywords.none-assigned')}
                </span>
              ) : (
                pendingKeywords.map((assignedKeyword) => (
                  <KeywordBadge
                    key={assignedKeyword}
                    keyword={assignedKeyword}
                    onRemove={() => handleRemove(assignedKeyword)}
                  />
                ))
              )}
            </div>
          </div>

          {/* Add keyword */}
          <div>
            <LabelT text={t('train-ai.keywords.section-label')} />
            <div className='flex flex-col gap-1 mt-1'>
              <div className='flex flex-col gap-x-5 sm:flex-row sm:items-center'>
                <RadioInputT
                  id='keywordMode-select'
                  name='keywordMode'
                  label={t('train-ai.keywords.select-existing')}
                  checked={keywordMode === 'select'}
                  onChange={() => setKeywordMode('select')}
                  className='my-0'
                  labelClassName='!text-xs'
                />
                {/* Only allow creating a new keyword if the user has the create permission */}
                {canCreateKeyword && (
                  <RadioInputT
                    id='keywordMode-new'
                    name='keywordMode'
                    label={t('train-ai.keywords.add-new')}
                    checked={keywordMode === 'new'}
                    onChange={() => setKeywordMode('new')}
                    className='my-0'
                    labelClassName='!text-xs'
                  />
                )}
              </div>
              <div className='flex gap-2 items-start'>
                {keywordMode === 'select' ? (
                  <SelectInputT
                    parentClassName='w-full'
                    values={selectOptions}
                    value={selectedKeyword}
                    onChange={(event) =>
                      setSelectedKeyword(
                        (event.target as HTMLSelectElement).value,
                      )
                    }
                  />
                ) : (
                  <InputT
                    type='text'
                    parentClassName='!mb-0 flex-1'
                    placeholder={t('train-ai.keywords.new-placeholder')}
                    error={newKeywordError}
                    options={{
                      value: newKeyword,
                      onChange: handleNewKeywordChange,
                    }}
                  />
                )}
                <ButtonT
                  type='button'
                  styling='primary'
                  size='xs'
                  rounding='md'
                  onClick={handleAdd}>
                  {t('train-ai.keywords.add')}
                </ButtonT>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className='tailwind-modal-footer mt-6'>
        <ButtonT
          onClick={() => hideModal()}
          type='button'
          styling='cancel'
          size='xs'
          rounding='md'>
          {t('general.buttons.cancel')}
        </ButtonT>
        <ButtonT
          type='button'
          styling='primary'
          size='xs'
          rounding='md'
          onClick={handleSave}>
          {t('general.buttons.save')}
        </ButtonT>
      </div>
    </div>
  );
};

export default ConversationKeywordsModal;
